// Competitors by audience (docs/CREATIVE_V2.md PR 3): the marker on every persona and the
// drawer behind it. Evidence comes from /api/creative/v2/competitors: the gap step's
// competitor ads scored against each ratified persona, resolved to registry ids. The drawer
// lists the ads with landing page, first and last seen, and the three lines behind the score.
// Annotation only: nothing here touches spend or results.
import { createContext, useContext, useState, type ReactNode } from 'react'
import { useApi } from '../../lib/api'
import { useCreativeV2 } from '../../lib/creativeV2'
import { Drawer, Word, Caveat } from '../../kit'

export interface CompetitorPersona {
  persona_id: string; persona_name: string | null; category: string; n_comp_ads: number; comp_weight: number
  competitors: string[]; stages: Record<string, number>; evidence_ads: number; weight_share_pct: number
}
export interface CompetitorAd {
  ad_fingerprint: string; competitor_slug?: string | null; competitor_name?: string | null; headline?: string | null
  ad_creative_body?: string | null; thumbnail_url: string; image_url?: string | null; landing_page: string | null
  landing_page_kind: 'landing_page' | 'ad_library'; first_seen: string | null; last_seen: string | null
  days_running?: number | null; similarity?: number | null; awareness_stage?: string | null; rationale: string[]
  asset_type?: string | null; visual_format?: string | null; ad_snapshot_url?: string | null
}
interface Resp { status: string; message?: string; personas: CompetitorPersona[]; ads: CompetitorAd[]; unmapped?: { rows: number; n_comp_ads: number } }

const Ctx = createContext<(personaId: string) => void>(() => {})
export const useOpenCompetitors = () => useContext(Ctx)

/** The per-persona index behind every marker; one request, cached by react-query. */
export function useCompetitorIndex(): Map<string, CompetitorPersona> {
  const v2 = useCreativeV2()
  const r = useApi<Resp>('/api/creative/v2/competitors', { enabled: v2, retry: false })
  const m = new Map<string, CompetitorPersona>()
  if (r.data?.status === 'success') r.data.personas.forEach(p => m.set(p.persona_id, p))
  return m
}

export function CompetitorDrawerProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<string | null>(null)
  return (
    <Ctx.Provider value={setOpen}>
      {children}
      {open && <CompetitorDrawer key={open} personaId={open} onClose={() => setOpen(null)} />}
    </Ctx.Provider>
  )
}

const day = (s: string | null | undefined) => (s || '').slice(0, 10)
const stageWord = (s: string) => s.replace(/_/g, ' ')

/** The marker: how many competitor ads sit on this audience, and who. Opens the drawer. */
export function CompetitorMark({ p, compact = false }: { p: CompetitorPersona | undefined; compact?: boolean }) {
  const open = useOpenCompetitors()
  if (!p || !p.n_comp_ads) return compact ? null : <span className="kit-sub">No competitor ads read on this audience.</span>
  const who = p.competitors.slice(0, 3).map(c => c.replace(/_/g, ' ')).join(', ') + (p.competitors.length > 3 ? ` +${p.competitors.length - 3}` : '')
  return (
    <button className={`kit-mark ${p.weight_share_pct >= 50 ? 'hot' : p.weight_share_pct >= 15 ? 'warm' : ''}`}
            title={`${p.n_comp_ads} competitor ads on this audience · ${who}. Click for the ads, landing pages and the read behind each.`}
            onClick={e => { e.stopPropagation(); open(p.persona_id) }}>
      <span className="kit-mark-n">{p.n_comp_ads}</span> competitor ad{p.n_comp_ads === 1 ? '' : 's'}{compact ? '' : ` · ${who}`}
    </button>
  )
}

function CompetitorDrawer({ personaId, onClose }: { personaId: string; onClose: () => void }) {
  const r = useApi<Resp>(`/api/creative/v2/competitors?persona=${encodeURIComponent(personaId)}`, { retry: false })
  const x = r.data?.status === 'success' ? r.data : null
  const p = x?.personas.find(q => q.persona_id === personaId)
  const stages = p ? Object.entries(p.stages).sort((a, b) => b[1] - a[1]) : []
  return (
    <Drawer title={p ? `Competitors · ${p.persona_name || p.persona_id}` : 'Competitors'} onClose={onClose}>
      {r.isLoading && <span className="kit-sub">Reading competitor ads for this audience…</span>}
      {!r.isLoading && !x && <span className="kit-sub">{r.data?.message || 'Competitor evidence is not available for this account.'}</span>}
      {x && !p && <span className="kit-sub">No competitor ads were read on this audience.</span>}
      {x && p && <>
        <div className="kit-card">
          <span className="k">{p.category} · {p.n_comp_ads} competitor ads</span>
          <div className="kit-kv"><span>competitors</span><b>{p.competitors.map(c => c.replace(/_/g, ' ')).join(', ')}</b></div>
          <div className="kit-kv"><span>by stage</span><b>{stages.map(([s, n]) => `${stageWord(s)} ${n}`).join(' · ')}</b></div>
          <div className="kit-kv"><span>flight weight</span><b>{Math.round(p.comp_weight).toLocaleString()} ad-days · {p.weight_share_pct}% of the busiest audience</b></div>
        </div>
        <span className="k">Ads · {x.ads.length} closest to this audience</span>
        {x.ads.map(a => (
          <div className="kit-card kit-comp" key={a.ad_fingerprint}>
            <div className="kit-comp-row">
              <a className="kit-thumb" style={{ width: 72, height: 90 }} href={a.ad_snapshot_url || a.landing_page || '#'} target="_blank" rel="noreferrer" title="Open in the Ad Library">
                <img src={a.thumbnail_url} alt="" loading="lazy" onError={e => { const el = e.target as HTMLImageElement; if (a.image_url && el.src !== a.image_url) el.src = a.image_url; else el.style.visibility = 'hidden' }} />
              </a>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div><b>{a.competitor_name || a.competitor_slug}</b> {a.awareness_stage && <Word tone="quiet">{stageWord(a.awareness_stage)}</Word>}</div>
                {(a.headline || a.ad_creative_body) && <div className="kit-comp-head">{a.headline || (a.ad_creative_body || '').slice(0, 120)}</div>}
                <div className="kit-sub">
                  {a.first_seen ? `first seen ${day(a.first_seen)}` : 'first seen unknown'} · {a.last_seen ? `last seen ${day(a.last_seen)}` : 'last seen unknown'}
                  {a.days_running != null && ` · ${a.days_running} days`}{a.similarity != null && ` · match ${Math.round(a.similarity * 100)}%`}
                </div>
                {a.landing_page && (
                  <a className="kit-btn sm" style={{ marginTop: 6 }} href={a.landing_page} target="_blank" rel="noreferrer">
                    {a.landing_page_kind === 'landing_page' ? 'Landing page ↗' : 'Ad Library page ↗'}
                  </a>
                )}
              </div>
            </div>
            <ol className="kit-rationale">{a.rationale.map((line, i) => <li key={i}>{line}</li>)}</ol>
          </div>
        ))}
        <Caveat>Competitor ads come from the public Ad Library, labelled the same way as yours and matched to this audience by similarity. The read is about where competitors are spending flight time, never about their results. Landing pages are the links in their ads; when the scrape has none, the Ad Library page is shown instead.</Caveat>
      </>}
    </Drawer>
  )
}
